// src/pages/GuidePage.tsx
// Community guide page - public, auth-aware, standalone (no sidebar)

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// ─── Types ───────────────────────────────────────────────────────────────────

type Difficulty = 'beginner' | 'intermediate' | 'advanced' | 'expert';

interface PathLevel {
  number: number;
  difficulty: Difficulty;
  title: string;
  subtitle: string;
  prompt: string;
  tryAlso: string;
}

interface LearningPath {
  id: string;
  title: string;
  description: string;
  levels: PathLevel[];
  intro: string;
  languages: string[];
  levelCount: number;
  duration: string;
  isPopular: boolean;
  lang: string; // filter key
}

interface Recipe {
  id: string;
  title: string;
  description: string;
  tags: string[];
  difficulty: Difficulty;
  isPopular: boolean;
  lang: string;
  promptCount: number;
  detail: RecipeDetail;
}

interface RecipeDetail {
  problem: string;
  steps: { title: string; description: string }[];
  prompts: { label: string; text: string }[];
  result: string;
  duration: string;
  language: string;
}

interface StarterCard {
  id: string;
  title: string;
  description: string;
  tags: string[];
  promptCount: number;
  duration: string;
  prompts: StarterPrompt[];
  intro: string;
}

interface StarterPrompt {
  number: number;
  difficulty: Difficulty;
  title: string;
  subtitle: string;
  prompt: string;
  teaches: string;
}

// ─── Data: How to Start Cards ────────────────────────────────────────────────

const starters: StarterCard[] = [
  {
    id: 'chat',
    title: 'Chat',
    description: 'Ask anything, get runnable code. Write programs, process data, solve problems \u2014 execute instantly and see results.',
    tags: ['Code', 'Execute', 'Learn'],
    promptCount: 3,
    duration: '~5 min',
    intro: "Paste any of these prompts into Codemate chat. You'll get runnable code with output \u2014 click Execute to see it work instantly. Works with Python, JavaScript, Java, Go, Rust, C, and C++.",
    prompts: [
      { number: 1, difficulty: 'beginner', title: 'Your First Program', subtitle: 'Go \u2022 Logic + execution', prompt: '"I want to understand how loops and conditionals work in Go \u2014 show me by checking if a number is prime"', teaches: "You'll get runnable code with output \u2014 click Execute to see it work." },
      { number: 2, difficulty: 'intermediate', title: 'Data Processing', subtitle: 'Rust \u2022 Generate + analyze', prompt: '"Help me understand ownership and borrowing in Rust \u2014 use a simple example that actually makes it click"', teaches: 'Instant data processing \u2014 Codemate compiles and runs Rust for you.' },
      { number: 3, difficulty: 'beginner', title: 'Learn a Concept', subtitle: 'JavaScript \u2022 Explain + code', prompt: '"I\'m confused about let, const, and var in JavaScript \u2014 when do I use which one? Show me with examples I can run"', teaches: 'Get clear explanations with executable code \u2014 learn by reading and running.' },
    ],
  },
  {
    id: 'bookmate',
    title: 'Bookmate',
    description: 'Generate full tutorials with code you can run, save as PDF, or share. Structured lessons on any topic \u2014 like your own textbook.',
    tags: ['Tutorials', 'PDF', 'Share'],
    promptCount: 3,
    duration: '~5 min',
    intro: "Paste any of these into Bookmate. You'll get a full tutorial with runnable code blocks \u2014 save as PDF or share with classmates. Works with Python, JavaScript, Java, Go, Rust, C, and C++.",
    prompts: [
      { number: 1, difficulty: 'beginner', title: 'OOP Basics', subtitle: 'Java \u2022 Structured lesson', prompt: '"Explain how classes and objects work in Java with examples I can run and learn from"', teaches: 'Generates a full tutorial with code blocks you can run, save as PDF, or share.' },
      { number: 2, difficulty: 'intermediate', title: 'Pointers & Memory', subtitle: 'C++ \u2022 Deep dive', prompt: '"I want to understand how pointers work in C++ \u2014 explain it step by step with examples I can follow"', teaches: 'Get a structured, book-like tutorial you can revisit anytime.' },
      { number: 3, difficulty: 'beginner', title: 'Concurrency Basics', subtitle: 'Go \u2022 Learn + practice', prompt: '"I want to learn concurrency in Go from scratch \u2014 teach me goroutines and channels with examples I can practice"', teaches: 'Tutorials with exercises \u2014 learn the concept, then practice it right away.' },
    ],
  },
  {
    id: 'workflow',
    title: 'Workflow',
    description: 'One prompt to download datasets, search the web, and generate visualizations. Perfect for assignments, projects, and data analysis.',
    tags: ['Datasets', 'Charts', 'CSV Export'],
    promptCount: 5,
    duration: '~5 min',
    intro: 'Paste any of these into Workflow. The agent finds the dataset, downloads it, generates charts, and gives you a CSV \u2014 all in one prompt.',
    prompts: [
      { number: 1, difficulty: 'beginner', title: 'Iris Dataset Visualization', subtitle: 'Classic ML \u2022 Download + scatter plot', prompt: '"Download the Iris dataset and create a scatter plot of sepal_length vs petal_length colored by species"', teaches: 'Workflow finds famous datasets instantly and generates publication-ready charts.' },
      { number: 2, difficulty: 'beginner', title: 'Titanic Survival Analysis', subtitle: 'ML \u2022 Multi-chart grid', prompt: '"Download the Titanic dataset and create a 2x2 grid: survival by class, age distribution, fare box plot, and gender breakdown"', teaches: 'One prompt generates multiple visualizations from a single dataset.' },
      { number: 3, difficulty: 'intermediate', title: 'Bitcoin Price Trends', subtitle: 'Time series \u2022 Dual-axis chart', prompt: '"Download Bitcoin price history and create a dual-axis chart with price and 30-day moving average"', teaches: 'Time series analysis with moving averages \u2014 great for finance projects.' },
      { number: 4, difficulty: 'intermediate', title: 'University Search & Export', subtitle: 'Web search \u2022 CSV export', prompt: '"Find top 100 universities for computer science and export as CSV with rankings, location, and tuition fees"', teaches: 'When data isn\'t in a dataset, Workflow searches the web and structures it for you.' },
      { number: 5, difficulty: 'beginner', title: 'Wine Quality Analysis', subtitle: 'Box plot \u2022 Group comparison', prompt: '"Download the Wine Quality dataset and create a box plot of alcohol content by quality rating"', teaches: 'Compare distributions across groups \u2014 perfect for research projects.' },
    ],
  },
];

// ─── Data: Learning Paths ────────────────────────────────────────────────────

const pythonLevels: PathLevel[] = [
  { number: 1, difficulty: 'beginner', title: 'Basic Logic \u2014 Prime Checker', subtitle: 'Teaches: conditionals, loops, edge cases', prompt: '"I want to understand how loops and conditionals work in Python \u2014 show me by checking if a number is prime"', tryAlso: 'Try the same in: JavaScript, Java, Go, C++' },
  { number: 2, difficulty: 'beginner', title: 'Input + Real Use \u2014 Pass/Fail Calculator', subtitle: 'Teaches: input handling, lists, decision logic', prompt: '"Help me understand how lists work in Python by building something that takes student marks and calculates results"', tryAlso: 'Try the same in: Java, C++, Rust' },
  { number: 3, difficulty: 'beginner', title: 'Functions + Reusability \u2014 Stats Calculator', subtitle: 'Teaches: functions, return values, dictionaries', prompt: '"Teach me how to write reusable functions in Python \u2014 something that takes data in and returns useful results"', tryAlso: 'Try the same in: JavaScript, Go, Rust' },
  { number: 4, difficulty: 'intermediate', title: 'Data Structures \u2014 Stack Implementation', subtitle: 'Teaches: stack data structure, state tracking', prompt: '"I want to understand how stacks work \u2014 can you show me how to build one from scratch in Python"', tryAlso: 'Try the same in: Java, C++, Go' },
  { number: 5, difficulty: 'intermediate', title: 'Problem Solving \u2014 Second Largest', subtitle: 'Teaches: algorithmic thinking, edge case handling', prompt: '"Give me an algorithmic challenge in Python that makes me think \u2014 something I can solve without using built-in shortcuts"', tryAlso: 'Try the same in: C, C++, Rust' },
  { number: 6, difficulty: 'advanced', title: 'File Generation + Data Processing', subtitle: 'Teaches: CSV creation, file I/O, data processing', prompt: '"I want to learn file handling in Python \u2014 show me how to create a CSV, write data to it, and read it back"', tryAlso: 'Try the same in: JavaScript (Node.js), Go' },
  { number: 7, difficulty: 'advanced', title: 'JSON + Mock Data Processing', subtitle: 'Teaches: JSON handling, filtering, sorting, formatted output', prompt: '"How do I work with JSON data in Python? Show me how to parse, filter, and display results from a dataset"', tryAlso: 'Try the same in: JavaScript, Go, Rust' },
  { number: 8, difficulty: 'expert', title: 'Mini Project \u2014 Student Grade Tracker', subtitle: 'Teaches: project structure, menus, combining concepts', prompt: '"I want to build a small project in Python that combines what I\'ve learned so far \u2014 something interactive I can actually use"', tryAlso: 'Try the same in: Java, C++' },
  { number: 9, difficulty: 'expert', title: 'Optimization \u2014 Finding Duplicates at Scale', subtitle: 'Teaches: performance comparison, time complexity', prompt: '"Show me why some approaches are faster than others in Python \u2014 I want to see the time difference with real numbers"', tryAlso: 'Try the same in: Go, Rust, C++' },
  { number: 10, difficulty: 'expert', title: 'Multi-Concept Capstone \u2014 Exam Analytics', subtitle: 'Teaches: combining everything', prompt: '"I want to build something meaningful in Python that uses functions, data structures, file handling, and clean output all together"', tryAlso: 'Try the same in: Java, Go, JavaScript' },
];

const goLevels: PathLevel[] = [
  { number: 1, difficulty: 'beginner', title: 'Basic Logic \u2014 Prime Checker', subtitle: 'Teaches: Go syntax, loops, conditionals, fmt', prompt: '"I want to understand how loops and conditionals work in Go \u2014 show me by checking if a number is prime"', tryAlso: 'Try the same in: Python, Rust, C++' },
  { number: 2, difficulty: 'beginner', title: 'Input + Real Use \u2014 Pass/Fail with Slices', subtitle: 'Teaches: slices, range loops, basic arithmetic', prompt: '"Help me understand slices in Go by building a small program that tracks student marks and calculates averages"', tryAlso: 'Try the same in: Python, Java, Rust' },
  { number: 3, difficulty: 'beginner', title: 'Functions + Multiple Returns', subtitle: "Teaches: multiple return values, Go's signature feature", prompt: '"Show me how Go handles multiple return values from a function \u2014 maybe something that analyzes a list of numbers"', tryAlso: 'Try the same in: Python (using dict), Rust (using tuple)' },
  { number: 4, difficulty: 'intermediate', title: 'Structs + Methods', subtitle: "Teaches: structs, methods, Go's approach to OOP", prompt: '"I want to learn how Go does object-oriented programming with structs and methods \u2014 can you walk me through it with a practical example"', tryAlso: 'Try the same in: Java (class), Rust (struct + impl), C++ (class)' },
  { number: 5, difficulty: 'intermediate', title: 'Maps + Word Frequency', subtitle: 'Teaches: maps, string manipulation, sort.Slice', prompt: '"Teach me how maps work in Go by building something that counts word frequency in a block of text"', tryAlso: 'Try the same in: Python (Counter), JavaScript, Rust (HashMap)' },
  { number: 6, difficulty: 'advanced', title: 'File Generation + CSV', subtitle: 'Teaches: encoding/csv, os.Create, file I/O, tabwriter', prompt: '"I want to learn file handling in Go \u2014 show me how to create a CSV, write data to it, and read it back"', tryAlso: 'Try the same in: Python, JavaScript (Node.js)' },
  { number: 7, difficulty: 'advanced', title: 'JSON + Data Processing', subtitle: 'Teaches: encoding/json, struct tags, json.Unmarshal', prompt: '"How do I work with JSON in Go? Show me how to parse and filter data from a JSON structure"', tryAlso: 'Try the same in: Python, Rust (serde), JavaScript' },
  { number: 8, difficulty: 'expert', title: 'Concurrency \u2014 Goroutines + Channels', subtitle: "Teaches: goroutines, channels \u2014 Go's killer feature", prompt: '"I keep hearing goroutines are what make Go special \u2014 teach me how they work with channels using a hands-on example"', tryAlso: 'Try the same in: Rust (threads), Java (threads), Python (threading)' },
  { number: 9, difficulty: 'expert', title: 'Performance + Benchmarking', subtitle: 'Teaches: time package, algorithmic efficiency', prompt: '"Show me how to measure and compare performance of two different approaches to the same problem in Go"', tryAlso: 'Try the same in: Rust, C++, Python' },
  { number: 10, difficulty: 'expert', title: 'Multi-Concept Capstone \u2014 Exam Analytics', subtitle: 'Teaches: combining structs, slices, maps, sorting, file I/O', prompt: '"I want to combine everything I\'ve learned in Go \u2014 structs, slices, maps, file I/O \u2014 into one meaningful project I can run"', tryAlso: 'Try the same in: Python, Java, Rust' },
];

const rustLevels: PathLevel[] = [
  { number: 1, difficulty: 'beginner', title: 'Basic Logic \u2014 Prime Checker', subtitle: 'Teaches: Rust syntax, loops, println! macro', prompt: '"I\'m new to Rust \u2014 show me how the basics work by solving a simple problem with loops and conditionals"', tryAlso: 'Try the same in: Python, Go, C++' },
  { number: 2, difficulty: 'beginner', title: 'Ownership + Variables', subtitle: 'Teaches: Vec, ownership, borrowing, iterators', prompt: '"Help me understand ownership and borrowing in Rust \u2014 use a simple example that actually makes it click"', tryAlso: 'Try the same in: Python (list), Go (slice), C++ (vector)' },
  { number: 3, difficulty: 'beginner', title: 'Functions + Tuples + Option', subtitle: 'Teaches: slices, tuples, Option type, null safety', prompt: '"Show me how functions and return types work in Rust \u2014 especially how it handles cases where there might be no result"', tryAlso: 'Try the same in: Go (multiple returns), Python (dict), Java' },
  { number: 4, difficulty: 'intermediate', title: 'Structs + impl Blocks', subtitle: 'Teaches: structs, impl, String vs &str, methods', prompt: '"I want to understand how Rust does object-oriented programming with structs and impl blocks \u2014 walk me through it"', tryAlso: 'Try the same in: Go (struct + methods), Java (class), C++ (class)' },
  { number: 5, difficulty: 'intermediate', title: 'HashMap + Iterators', subtitle: 'Teaches: HashMap, entry API, iterators, collect', prompt: '"Teach me how HashMaps and iterators work in Rust by building something that processes text"', tryAlso: 'Try the same in: Go (map), Python (Counter), JavaScript' },
  { number: 6, difficulty: 'advanced', title: 'File Generation + String Processing', subtitle: 'Teaches: std::fs, file I/O, parse, error handling', prompt: '"I want to learn file handling in Rust \u2014 show me how to create a file, write structured data, and read it back"', tryAlso: 'Try the same in: Python, Go, JavaScript (Node.js)' },
  { number: 7, difficulty: 'advanced', title: 'Enums + Pattern Matching', subtitle: "Teaches: enums, match \u2014 Rust's most powerful feature", prompt: '"I hear enums in Rust are way more powerful than other languages \u2014 show me what makes them special with a practical example"', tryAlso: 'Try the same in: Java (enum), Go (constants + switch), Python (if-elif)' },
  { number: 8, difficulty: 'expert', title: 'Error Handling + Result', subtitle: 'Teaches: Result, custom errors, ? operator', prompt: '"Teach me how Rust handles errors without try-catch \u2014 I want to understand Result and the ? operator with a real example"', tryAlso: 'Try the same in: Go (error return), Java (try-catch), Python (try-except)' },
  { number: 9, difficulty: 'expert', title: 'Performance + HashSet', subtitle: 'Teaches: HashSet, Instant for timing, algorithmic efficiency', prompt: '"Show me how to benchmark two different approaches in Rust and see which one is faster with real timing"', tryAlso: 'Try the same in: Go, C++, Python' },
  { number: 10, difficulty: 'expert', title: 'Multi-Concept Capstone \u2014 Exam Analytics', subtitle: 'Teaches: combining structs, Vec, HashMap, iterators, file I/O', prompt: '"I want to build a complete project in Rust that brings together structs, collections, file I/O, and error handling"', tryAlso: 'Try the same in: Go, Python, Java' },
];

const learningPaths: LearningPath[] = [
  {
    id: 'rust', title: 'Progressive Rust Learning', description: "Master ownership, pattern matching, and error handling. Rust's safety guarantees explained through progressive, runnable prompts.",
    levels: rustLevels,
    intro: "Master Rust's ownership model, pattern matching, and error handling from scratch. Each level introduces a core Rust concept with a runnable prompt \u2014 paste it in Codemate, run the code, and see why Rust is loved by developers.",
    languages: ['Rust', 'Python', 'Go'], levelCount: 10, duration: '~2.5 hours', isPopular: true, lang: 'rust',
  },
  {
    id: 'go', title: 'Progressive Go Learning', description: "From basic syntax to goroutines and channels. Learn Go's simplicity and concurrency model \u2014 run every prompt instantly.",
    levels: goLevels,
    intro: "Learn Go from basic syntax to goroutines and channels. Each level gives you a prompt \u2014 paste it in Codemate, run the code, see the output. Go's simplicity makes it perfect for beginners who want to write fast, concurrent programs.",
    languages: ['Go', 'Python', 'Rust'], levelCount: 10, duration: '~2 hours', isPopular: true, lang: 'go',
  },
  {
    id: 'python', title: 'Progressive Python Learning', description: 'From prime numbers to full analytics systems. Each level builds on the last \u2014 write code, run it, see results, level up.',
    levels: pythonLevels, intro: 'Start with a simple prime checker and finish building a full analytics system. Each level gives you a prompt \u2014 paste it in Codemate, run the code, see the output. Every prompt builds on what you just learned.',
    languages: ['Python'], levelCount: 10, duration: '~2 hours', isPopular: false, lang: 'python',
  },
];

// ─── Data: Recipes ───────────────────────────────────────────────────────────

const recipes: Recipe[] = [
  {
    id: 'course-csv', title: 'Find & Compare Top Free Python Courses', description: 'Search for free Python courses, compare ratings and platforms, export as a CSV to share with classmates.',
    tags: ['Python', 'CSV Export', 'Research'], difficulty: 'beginner', isPopular: true, lang: 'python', promptCount: 3,
    detail: {
      problem: 'Hundreds of Python courses online. You need a fast way to find the best free ones, compare ratings, and pick the right one \u2014 without spending hours searching.',
      steps: [
        { title: 'Research & Collect', description: 'Ask Codemate to find top-rated free Python courses across Coursera, edX, Udemy, and freeCodeCamp.' },
        { title: 'Structure & Compare', description: 'Organize into a comparison table with ratings, duration, and skill level.' },
        { title: 'Export to CSV', description: 'Generate a downloadable CSV to share with classmates or filter later.' },
      ],
      prompts: [
        { label: 'Prompt 1 \u2014 Research', text: '"Find the best free Python courses for beginners \u2014 I want to compare what\'s out there on Coursera, Udemy, and other platforms"' },
        { label: 'Prompt 2 \u2014 Compare', text: '"Now organize those into a comparison table sorted by rating, and tell me which ones are best for absolute beginners"' },
        { label: 'Prompt 3 \u2014 Export', text: '"Export everything into a CSV I can download and share with my classmates"' },
      ],
      result: 'A downloadable CSV with 10 courses compared side-by-side \u2014 ready to filter, sort, or share with classmates.',
      duration: '~10 min', language: 'Python',
    },
  },
  {
    id: 'study-timer', title: 'Build a Pomodoro Study Timer', description: 'Create a CLI Pomodoro timer with customizable work/break intervals. Run it and manage your study sessions.',
    tags: ['Python', 'CLI Tool', 'Productivity'], difficulty: 'beginner', isPopular: false, lang: 'python', promptCount: 2,
    detail: {
      problem: 'Staying focused while studying is hard. You need a simple timer that enforces work/break intervals without installing anything.',
      steps: [
        { title: 'Build the Timer', description: 'Create a Python CLI Pomodoro timer with customizable work and break durations.' },
        { title: 'Add Features', description: 'Add session counting, sound alerts, and a summary at the end.' },
      ],
      prompts: [
        { label: 'Prompt 1 \u2014 Build', text: '"Build me a Pomodoro study timer in Python that runs in the terminal with 25-minute work and 5-minute break intervals"' },
        { label: 'Prompt 2 \u2014 Enhance', text: '"Add session tracking and a summary of total study time when I\'m done"' },
      ],
      result: 'A working CLI Pomodoro timer you can run anytime to manage your study sessions.',
      duration: '~8 min', language: 'Python',
    },
  },
  {
    id: 'todo-app', title: 'Build a To-Do App with Local Storage', description: 'Create a functional to-do list that saves in the browser. Add, complete, delete tasks \u2014 run it live.',
    tags: ['JavaScript', 'HTML/CSS', 'DOM'], difficulty: 'beginner', isPopular: false, lang: 'javascript', promptCount: 3,
    detail: {
      problem: 'You want to learn DOM manipulation and browser storage by building something you can actually use.',
      steps: [
        { title: 'Create the UI', description: 'Build the HTML structure and CSS styling for a clean to-do list.' },
        { title: 'Add Interactivity', description: 'Use JavaScript to add, complete, and delete tasks.' },
        { title: 'Persist Data', description: 'Save tasks to localStorage so they survive page refreshes.' },
      ],
      prompts: [
        { label: 'Prompt 1 \u2014 UI', text: '"Create a clean, dark-themed to-do list app using HTML, CSS, and JavaScript"' },
        { label: 'Prompt 2 \u2014 Logic', text: '"Add the ability to mark tasks complete, delete them, and filter by status"' },
        { label: 'Prompt 3 \u2014 Persist', text: '"Save everything to localStorage so tasks survive a page refresh"' },
      ],
      result: 'A fully functional to-do app that runs in the browser with persistent storage.',
      duration: '~12 min', language: 'JavaScript',
    },
  },
  {
    id: 'grade-dashboard', title: 'Visualize Your Grades as a Dashboard', description: 'Upload grades CSV, generate trend charts and GPA predictions, get an interactive dashboard you can run and share.',
    tags: ['Python', 'Visualization', 'Dashboard'], difficulty: 'intermediate', isPopular: true, lang: 'python', promptCount: 4,
    detail: {
      problem: 'You have semester grades but no easy way to visualize trends or predict your GPA trajectory.',
      steps: [
        { title: 'Generate Sample Data', description: 'Create a CSV with realistic grade data across semesters.' },
        { title: 'Analyze Trends', description: 'Calculate GPA per semester and identify trends.' },
        { title: 'Build Charts', description: 'Generate matplotlib charts showing grade distribution and GPA trends.' },
        { title: 'Create Dashboard', description: 'Combine everything into an interactive dashboard.' },
      ],
      prompts: [
        { label: 'Prompt 1 \u2014 Data', text: '"Generate a CSV with sample student grades across 4 semesters with different subjects"' },
        { label: 'Prompt 2 \u2014 Analyze', text: '"Analyze the grade data and calculate GPA per semester with trend analysis"' },
        { label: 'Prompt 3 \u2014 Visualize', text: '"Create matplotlib charts showing grade distribution and GPA trends over time"' },
        { label: 'Prompt 4 \u2014 Dashboard', text: '"Combine everything into a single dashboard view with summary statistics"' },
      ],
      result: 'An interactive grade dashboard with charts, trends, and GPA predictions.',
      duration: '~15 min', language: 'Python',
    },
  },
  {
    id: 'student-db', title: 'Design a Student Records Database', description: 'Design tables, write queries for enrollment, grades, and transcripts. Run each query and see results.',
    tags: ['SQL', 'Database Design', 'Queries'], difficulty: 'intermediate', isPopular: false, lang: 'sql', promptCount: 4,
    detail: {
      problem: 'You need to learn database design and SQL queries with a relatable example you can actually run.',
      steps: [
        { title: 'Design Schema', description: 'Create tables for students, courses, enrollments, and grades.' },
        { title: 'Insert Data', description: 'Populate tables with realistic sample data.' },
        { title: 'Write Queries', description: 'Query for transcripts, GPA calculations, and enrollment reports.' },
        { title: 'Advanced Queries', description: 'Write JOINs, subqueries, and aggregations.' },
      ],
      prompts: [
        { label: 'Prompt 1 \u2014 Schema', text: '"Design a student records database with tables for students, courses, enrollments, and grades"' },
        { label: 'Prompt 2 \u2014 Data', text: '"Insert realistic sample data into all the tables"' },
        { label: 'Prompt 3 \u2014 Queries', text: '"Write queries to generate transcripts and calculate GPA for each student"' },
        { label: 'Prompt 4 \u2014 Advanced', text: '"Show me advanced queries with JOINs and subqueries to find top students per course"' },
      ],
      result: 'A complete student database with schema, data, and working queries.',
      duration: '~15 min', language: 'SQL',
    },
  },
  {
    id: 'university-scraper', title: 'Scrape & Rank 50 Universities by CS Program', description: 'Build a scraper, collect CS rankings and tuition data, visualize comparisons \u2014 run the full pipeline.',
    tags: ['Python', 'Web Scraping', 'Data Analysis'], difficulty: 'advanced', isPopular: false, lang: 'python', promptCount: 5,
    detail: {
      problem: 'You want to compare CS programs across universities but the data is scattered across multiple websites.',
      steps: [
        { title: 'Set Up Scraper', description: 'Build a web scraper to collect university CS program data.' },
        { title: 'Collect Data', description: 'Scrape rankings, tuition, acceptance rates from multiple sources.' },
        { title: 'Clean & Process', description: 'Normalize and clean the scraped data.' },
        { title: 'Analyze & Rank', description: 'Create a composite ranking based on multiple factors.' },
        { title: 'Visualize', description: 'Generate comparison charts and export results.' },
      ],
      prompts: [
        { label: 'Prompt 1 \u2014 Setup', text: '"Build a Python web scraper that can collect university CS program data"' },
        { label: 'Prompt 2 \u2014 Collect', text: '"Scrape CS rankings, tuition costs, and acceptance rates for 50 universities"' },
        { label: 'Prompt 3 \u2014 Clean', text: '"Clean and normalize all the scraped data into a consistent format"' },
        { label: 'Prompt 4 \u2014 Rank', text: '"Create a composite ranking that weighs CS ranking, tuition, and acceptance rate"' },
        { label: 'Prompt 5 \u2014 Visualize', text: '"Generate comparison charts and export the final rankings as CSV"' },
      ],
      result: 'A ranked list of 50 CS programs with charts comparing tuition, rankings, and acceptance rates.',
      duration: '~20 min', language: 'Python',
    },
  },
  {
    id: 'weather-api', title: 'Build a Weather App with Public APIs', description: 'Connect to weather APIs, handle async requests, display a responsive dashboard \u2014 execute and iterate.',
    tags: ['JavaScript', 'APIs', 'Async'], difficulty: 'advanced', isPopular: false, lang: 'javascript', promptCount: 4,
    detail: {
      problem: 'You want to learn API integration and async JavaScript by building a real weather dashboard.',
      steps: [
        { title: 'API Setup', description: 'Connect to a free weather API and fetch data.' },
        { title: 'Parse Response', description: 'Handle JSON responses and extract weather details.' },
        { title: 'Build UI', description: 'Create a responsive weather display with HTML/CSS.' },
        { title: 'Add Features', description: 'Add search, geolocation, and 5-day forecast.' },
      ],
      prompts: [
        { label: 'Prompt 1 \u2014 API', text: '"Show me how to fetch weather data from a free API using JavaScript"' },
        { label: 'Prompt 2 \u2014 Parse', text: '"Parse the weather API response and extract temperature, humidity, and conditions"' },
        { label: 'Prompt 3 \u2014 UI', text: '"Build a clean, responsive weather dashboard with the data"' },
        { label: 'Prompt 4 \u2014 Features', text: '"Add city search and a 5-day forecast view"' },
      ],
      result: 'A working weather dashboard that fetches live data from a public API.',
      duration: '~18 min', language: 'JavaScript',
    },
  },
  {
    id: 'research-pipeline', title: 'End-to-End Research Pipeline: Collect, Clean, Analyze & Report', description: 'Build a complete pipeline \u2014 scraping to PDF report. Run each stage, verify outputs, chain them together.',
    tags: ['Python', 'Data Science', 'Automation'], difficulty: 'expert', isPopular: true, lang: 'python', promptCount: 6,
    detail: {
      problem: 'Your research project needs data collection, cleaning, analysis, and reporting \u2014 all automated as a pipeline.',
      steps: [
        { title: 'Define Scope', description: 'Set up the research question and data sources.' },
        { title: 'Collect Data', description: 'Build scrapers or API connectors to gather raw data.' },
        { title: 'Clean & Transform', description: 'Process raw data into analysis-ready format.' },
        { title: 'Analyze', description: 'Run statistical analysis and generate insights.' },
        { title: 'Visualize', description: 'Create publication-quality charts and graphs.' },
        { title: 'Generate Report', description: 'Compile everything into a PDF report.' },
      ],
      prompts: [
        { label: 'Prompt 1 \u2014 Setup', text: '"Set up a Python research pipeline for analyzing student study habits data"' },
        { label: 'Prompt 2 \u2014 Collect', text: '"Generate realistic survey data about student study habits with 500 responses"' },
        { label: 'Prompt 3 \u2014 Clean', text: '"Clean the raw data \u2014 handle missing values, normalize formats, remove outliers"' },
        { label: 'Prompt 4 \u2014 Analyze', text: '"Run statistical analysis on the cleaned data and find correlations between study habits and GPA"' },
        { label: 'Prompt 5 \u2014 Visualize', text: '"Create publication-quality charts showing the key findings"' },
        { label: 'Prompt 6 \u2014 Report', text: '"Generate a PDF report with executive summary, methodology, findings, and charts"' },
      ],
      result: 'A complete research pipeline from raw data to a formatted PDF report.',
      duration: '~30 min', language: 'Python',
    },
  },
];

// ─── Filter Options ──────────────────────────────────────────────────────────

type CategoryFilter = 'all' | 'popular' | 'path' | 'recipe' | 'beginner' | 'intermediate' | 'advanced' | 'expert';
type LanguageFilter = 'all' | 'python' | 'javascript' | 'java' | 'cpp' | 'go' | 'rust' | 'sql' | 'html-css';

const categoryOptions: { value: CategoryFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'popular', label: 'Popular' },
];
const categoryOptions2: { value: CategoryFilter; label: string }[] = [
  { value: 'path', label: 'Learning Paths' },
  { value: 'recipe', label: 'Recipes' },
];
const difficultyOptions: { value: CategoryFilter; label: string }[] = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'expert', label: 'Expert' },
];

const languageOptions: { value: LanguageFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'python', label: 'Python' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'java', label: 'Java' },
  { value: 'cpp', label: 'C++' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
  { value: 'sql', label: 'SQL' },
  { value: 'html-css', label: 'HTML/CSS' },
];

// ─── Difficulty colors ───────────────────────────────────────────────────────

const difficultyColors: Record<Difficulty, { text: string; bg: string; border: string }> = {
  beginner:     { text: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400' },
  intermediate: { text: 'text-blue-400',  bg: 'bg-blue-400/10',  border: 'border-blue-400' },
  advanced:     { text: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400' },
  expert:       { text: 'text-red-400',   bg: 'bg-red-400/10',   border: 'border-red-400' },
};

// ─── Component ───────────────────────────────────────────────────────────────

const GuidePage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Filter state
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('all');
  const [activeLang, setActiveLang] = useState<LanguageFilter>('all');

  // Modal state
  const [activePathId, setActivePathId] = useState<string | null>(null);
  const [activeRecipeId, setActiveRecipeId] = useState<string | null>(null);
  const [activeStarterId, setActiveStarterId] = useState<string | null>(null);

  // Copy button state
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copyPrompt = useCallback((text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    });
  }, []);

  // Close modals on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActivePathId(null);
        setActiveRecipeId(null);
        setActiveStarterId(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (activePathId || activeRecipeId || activeStarterId) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [activePathId, activeRecipeId, activeStarterId]);

  // ─── Filtering logic ────────────────────────────────────────────────────

  const filteredPaths = learningPaths.filter(p => {
    const cat = activeCategory;
    let catMatch = cat === 'all' || cat === 'path' || (cat === 'popular' && p.isPopular);
    if (['beginner', 'intermediate', 'advanced', 'expert', 'recipe'].includes(cat)) catMatch = false;
    const langMatch = activeLang === 'all' || p.lang === activeLang;
    return catMatch && langMatch;
  });

  const filteredRecipes = recipes.filter(r => {
    const cat = activeCategory;
    let catMatch = cat === 'all' || cat === 'recipe' || cat === r.difficulty || (cat === 'popular' && r.isPopular);
    if (cat === 'path') catMatch = false;
    const langMatch = activeLang === 'all' || r.lang === activeLang;
    return catMatch && langMatch;
  });

  const totalVisible = filteredPaths.length + filteredRecipes.length;

  // ─── Lookup helpers ──────────────────────────────────────────────────────

  const activePath = activePathId ? learningPaths.find(p => p.id === activePathId) : null;
  const activeRecipe = activeRecipeId ? recipes.find(r => r.id === activeRecipeId) : null;
  const activeStarter = activeStarterId ? starters.find(s => s.id === activeStarterId) : null;

  // ─── Sub-components ──────────────────────────────────────────────────────

  const CopyButton: React.FC<{ text: string; copyKey: string }> = ({ text, copyKey }) => {
    const isCopied = copiedKey === copyKey;
    return (
      <button
        onClick={(e) => { e.stopPropagation(); copyPrompt(text, copyKey); }}
        className={`px-3 py-1.5 rounded-md border text-xs font-medium whitespace-nowrap transition-all shrink-0 ${
          isCopied
            ? 'text-green-400 border-green-400'
            : 'text-[hsl(0,0%,65%)] border-[hsl(0,0%,20%)] bg-[hsl(0,0%,15%)] hover:text-white hover:border-purple-500'
        }`}
      >
        {isCopied ? 'Copied!' : 'Copy'}
      </button>
    );
  };

  const LevelDots: React.FC<{ count: number }> = ({ count }) => {
    const getDifficulty = (n: number): Difficulty => {
      if (n <= 3) return 'beginner';
      if (n <= 5) return 'intermediate';
      if (n <= 7) return 'advanced';
      return 'expert';
    };
    return (
      <div className="flex items-center gap-1.5">
        {Array.from({ length: count }, (_, i) => {
          const d = getDifficulty(i + 1);
          const c = difficultyColors[d];
          return (
            <React.Fragment key={i}>
              {i > 0 && <div className="w-2 h-0.5 bg-[hsl(0,0%,20%)]" />}
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 ${c.border} ${c.text}`}>
                {i + 1}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[hsl(0,0%,9%)] text-[hsl(0,0%,98%)]" style={{ fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif" }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-8 py-4 border-b border-[hsl(0,0%,20%)] bg-[hsl(0,0%,11%)] sticky top-0 z-[100]">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-[hsl(0,0%,65%)] hover:text-white hover:bg-[hsl(0,0%,20%)] transition-all"
            title="Go back"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
          <div className="w-8 h-8 bg-[hsl(263,60%,50%)] rounded-lg flex items-center justify-center font-bold text-base">C</div>
          <div className="text-lg font-semibold">
            Cubemate <span className="text-[hsl(0,0%,65%)] font-normal ml-1">Community</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {isAuthenticated && user ? (
            <div className="flex items-center gap-2.5">
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.username)}&background=7c3aed&color=fff&size=32`}
                alt=""
                className="w-8 h-8 rounded-full border-2 border-[hsl(263,60%,50%)]"
              />
              <span className="text-sm font-medium">{user.username}</span>
            </div>
          ) : (
            <>
              <button
                onClick={() => navigate('/login')}
                className="px-5 py-2 rounded-lg border border-[hsl(0,0%,20%)] bg-transparent text-sm cursor-pointer transition-all hover:border-[hsl(263,60%,50%)] hover:text-[hsl(263,60%,50%)]"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-5 py-2 rounded-lg border-none bg-[hsl(263,60%,50%)] text-white text-sm font-medium cursor-pointer transition-all hover:bg-[hsl(263,60%,58%)]"
              >
                Get Started Free
              </button>
            </>
          )}
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section className="text-center py-14 px-8 max-w-[800px] mx-auto">
        <h1 className="text-4xl font-bold mb-3 tracking-tight">Community</h1>
        <p className="text-[17px] text-[hsl(0,0%,65%)] max-w-[580px] mx-auto leading-relaxed">
          What do you want to try today?
        </p>
        <p className="text-[13px] text-[hsl(0,0%,65%)] mt-2.5">
          Focused on logic, algorithms, data processing &amp; file generation. No network or database required.
        </p>
      </section>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="max-w-[1200px] mx-auto px-8 pb-8">
        {/* Browse row */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-[hsl(0,0%,65%)] font-semibold mr-1 whitespace-nowrap">Browse</span>
          {categoryOptions.map(o => (
            <button
              key={o.value}
              onClick={() => setActiveCategory(o.value)}
              className={`px-4 py-2 rounded-full border text-[13px] font-medium cursor-pointer transition-all whitespace-nowrap ${
                activeCategory === o.value
                  ? o.value === 'popular'
                    ? 'bg-pink-500/10 border-pink-500 text-pink-500'
                    : 'bg-[hsl(263,60%,50%,0.15)] border-[hsl(263,60%,50%)] text-[hsl(263,60%,50%)]'
                  : 'border-[hsl(0,0%,20%)] bg-transparent text-[hsl(0,0%,65%)] hover:border-[hsl(263,60%,50%)] hover:text-white'
              }`}
            >
              {o.label}
            </button>
          ))}
          <div className="w-px h-5 bg-[hsl(0,0%,20%)] mx-1" />
          {categoryOptions2.map(o => (
            <button
              key={o.value}
              onClick={() => setActiveCategory(o.value)}
              className={`px-4 py-2 rounded-full border text-[13px] font-medium cursor-pointer transition-all whitespace-nowrap ${
                activeCategory === o.value
                  ? 'bg-[hsl(263,60%,50%,0.15)] border-[hsl(263,60%,50%)] text-[hsl(263,60%,50%)]'
                  : 'border-[hsl(0,0%,20%)] bg-transparent text-[hsl(0,0%,65%)] hover:border-[hsl(263,60%,50%)] hover:text-white'
              }`}
            >
              {o.label}
            </button>
          ))}
          <div className="w-px h-5 bg-[hsl(0,0%,20%)] mx-1" />
          {difficultyOptions.map(o => (
            <button
              key={o.value}
              onClick={() => setActiveCategory(o.value)}
              className={`px-4 py-2 rounded-full border text-[13px] font-medium cursor-pointer transition-all whitespace-nowrap ${
                activeCategory === o.value
                  ? 'bg-[hsl(263,60%,50%,0.15)] border-[hsl(263,60%,50%)] text-[hsl(263,60%,50%)]'
                  : 'border-[hsl(0,0%,20%)] bg-transparent text-[hsl(0,0%,65%)] hover:border-[hsl(263,60%,50%)] hover:text-white'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {/* Language row */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-[hsl(0,0%,65%)] font-semibold mr-1 whitespace-nowrap">Language</span>
          {languageOptions.map(o => (
            <button
              key={o.value}
              onClick={() => setActiveLang(o.value)}
              className={`px-4 py-2 rounded-full border text-[13px] font-medium cursor-pointer transition-all whitespace-nowrap ${
                activeLang === o.value
                  ? 'bg-[hsl(263,60%,50%,0.15)] border-[hsl(263,60%,50%)] text-[hsl(263,60%,50%)]'
                  : 'border-[hsl(0,0%,20%)] bg-transparent text-[hsl(0,0%,65%)] hover:border-[hsl(263,60%,50%)] hover:text-white'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── How to Start ───────────────────────────────────────────────── */}
      <div className="max-w-[1200px] mx-auto px-8">
        <div className="text-[13px] font-semibold uppercase tracking-widest text-[hsl(0,0%,65%)] mb-4 pb-2 border-b border-[hsl(0,0%,20%)]">
          How to Start
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-2">
          {starters.map(s => (
            <div
              key={s.id}
              onClick={() => setActiveStarterId(s.id)}
              className="bg-[hsl(0,0%,12%)] border border-[hsl(0,0%,20%)] rounded-xl p-6 cursor-pointer transition-all flex flex-col gap-4 relative overflow-hidden hover:bg-[hsl(0,0%,14%)] hover:border-[hsl(0,0%,28%)] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.3)]"
              style={{ paddingTop: '27px' }}
            >
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[hsl(263,60%,50%)] to-[hsl(290,70%,60%)]" />
              <div className="flex items-start justify-between gap-3">
                <div className="text-lg font-bold leading-tight flex-1">{s.title}</div>
                <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[hsl(263,70%,60%,0.12)] text-[hsl(263,70%,60%)] uppercase tracking-wider whitespace-nowrap">
                  {s.promptCount} Prompts
                </span>
              </div>
              <p className="text-[13px] text-[hsl(0,0%,65%)] leading-relaxed">{s.description}</p>
              <div className="flex gap-1.5">
                {s.tags.map(t => (
                  <span key={t} className="text-[11px] px-2 py-0.5 rounded bg-[hsl(0,0%,15%)] text-[hsl(0,0%,65%)] font-medium">{t}</span>
                ))}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-[hsl(0,0%,20%)] mt-auto">
                <div className="flex items-center gap-2 text-xs text-[hsl(0,0%,65%)]">
                  <div className="w-5 h-5 rounded-full bg-[hsl(263,60%,50%,0.15)] flex items-center justify-center text-[9px] font-semibold text-[hsl(263,60%,50%)]">CM</div>
                  Cubemate
                </div>
                <div className="text-xs text-[hsl(0,0%,65%)]">{s.promptCount} prompts &bull; {s.duration}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main Content ───────────────────────────────────────────────── */}
      <div className="max-w-[1200px] mx-auto px-8 pb-16">

        {totalVisible === 0 && (
          <div className="text-center py-16 text-[hsl(0,0%,65%)] text-[15px]">
            No recipes match your filters. Try a different combination.
          </div>
        )}

        {/* Learning Paths */}
        {filteredPaths.length > 0 && (
          <>
            <div className="text-[13px] font-semibold uppercase tracking-widest text-[hsl(0,0%,65%)] mt-9 mb-4 pb-2 border-b border-[hsl(0,0%,20%)]">
              Learning Paths
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-2">
              {filteredPaths.map(p => (
                <div
                  key={p.id}
                  onClick={() => setActivePathId(p.id)}
                  className="bg-[hsl(0,0%,12%)] border border-[hsl(0,0%,20%)] rounded-xl p-6 cursor-pointer transition-all flex flex-col gap-4 relative overflow-hidden hover:bg-[hsl(0,0%,14%)] hover:border-[hsl(0,0%,28%)] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.3)]"
                  style={{ paddingTop: '27px' }}
                >
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[hsl(263,60%,50%)] to-[hsl(290,70%,60%)]" />
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-lg font-bold leading-tight flex-1">{p.title}</div>
                    <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[hsl(263,70%,60%,0.12)] text-[hsl(263,70%,60%)] uppercase tracking-wider whitespace-nowrap">
                      {p.levelCount} Levels
                    </span>
                  </div>
                  <p className="text-[13px] text-[hsl(0,0%,65%)] leading-relaxed">{p.description}</p>
                  <LevelDots count={p.levelCount} />
                  <div className="flex gap-1.5">
                    {p.languages.map(l => (
                      <span key={l} className="text-[11px] px-2 py-0.5 rounded bg-[hsl(0,0%,15%)] text-[hsl(0,0%,65%)] font-medium">{l}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-[hsl(0,0%,20%)] mt-auto">
                    <div className="flex items-center gap-2 text-xs text-[hsl(0,0%,65%)]">
                      <div className="w-5 h-5 rounded-full bg-[hsl(263,60%,50%,0.15)] flex items-center justify-center text-[9px] font-semibold text-[hsl(263,60%,50%)]">CT</div>
                      Cubemate Team
                    </div>
                    <div className="text-xs text-[hsl(0,0%,65%)]">{p.levelCount} prompts &bull; {p.duration}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Individual Recipes */}
        {filteredRecipes.length > 0 && (
          <>
            <div className="text-[13px] font-semibold uppercase tracking-widest text-[hsl(0,0%,65%)] mt-9 mb-4 pb-2 border-b border-[hsl(0,0%,20%)]">
              Individual Recipes
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-2">
              {filteredRecipes.map(r => (
                <div
                  key={r.id}
                  onClick={() => setActiveRecipeId(r.id)}
                  className="bg-[hsl(0,0%,12%)] border border-[hsl(0,0%,20%)] rounded-xl p-6 cursor-pointer transition-all flex flex-col gap-3.5 hover:bg-[hsl(0,0%,14%)] hover:border-[hsl(0,0%,28%)] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.3)]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-base font-semibold leading-snug flex-1">{r.title}</div>
                    <div className="flex gap-1.5 shrink-0">
                      {r.isPopular && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-pink-500/10 text-pink-500 uppercase tracking-wider">Popular</span>
                      )}
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider ${difficultyColors[r.difficulty].bg} ${difficultyColors[r.difficulty].text}`}>
                        {r.difficulty}
                      </span>
                    </div>
                  </div>
                  <p className="text-[13px] text-[hsl(0,0%,65%)] leading-relaxed">{r.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {r.tags.map(t => (
                      <span key={t} className="text-[11px] px-2 py-0.5 bg-[hsl(0,0%,15%)] rounded-md text-[hsl(0,0%,65%)]">{t}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-auto pt-3 border-t border-[hsl(0,0%,20%)]">
                    <div className="flex items-center gap-2 text-xs text-[hsl(0,0%,65%)]">
                      <div className="w-5 h-5 rounded-full bg-[hsl(263,60%,50%,0.15)] flex items-center justify-center text-[9px] font-semibold text-[hsl(263,60%,50%)]">CT</div>
                      Cubemate Team
                    </div>
                    <div className="text-[11px] text-[hsl(0,0%,65%)]">{r.promptCount} prompts</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Community CTA ──────────────────────────────────────────────── */}
      <section className="text-center py-16 px-8 border-t border-[hsl(0,0%,20%)] max-w-[600px] mx-auto">
        <h3 className="text-2xl font-semibold mb-3">
          Share Your How-To{' '}
          <span className="inline-block text-[11px] px-2 py-0.5 bg-orange-400/10 text-orange-400 rounded font-semibold ml-2 align-middle">COMING SOON</span>
        </h3>
        <p className="text-[hsl(0,0%,65%)] text-[15px] mb-6">
          Solved an interesting problem with Cubemate? Share your recipe and help other users learn. The best ones get featured.
        </p>
      </section>

      {/* ── Path Detail Modal ──────────────────────────────────────────── */}
      {activePath && (
        <div
          className="fixed inset-0 bg-black/70 z-[200] overflow-y-auto p-10 flex justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setActivePathId(null); }}
        >
          <div className="bg-[hsl(0,0%,9%)] border border-[hsl(0,0%,20%)] rounded-2xl max-w-[860px] w-full p-10 relative my-auto">
            <button
              onClick={() => setActivePathId(null)}
              className="absolute top-4 right-4 w-9 h-9 rounded-lg border border-[hsl(0,0%,20%)] bg-[hsl(0,0%,12%)] text-white text-lg cursor-pointer flex items-center justify-center transition-all hover:bg-[hsl(0,0%,15%)]"
            >
              &times;
            </button>

            <div className="mb-7">
              <div className="flex gap-1.5 mb-3">
                {activePath.isPopular && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-pink-500/10 text-pink-500 uppercase tracking-wider">Popular</span>
                )}
                <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[hsl(263,70%,60%,0.12)] text-[hsl(263,70%,60%)] uppercase tracking-wider">
                  {activePath.levelCount} Levels
                </span>
              </div>
              <h2 className="text-[26px] font-bold mb-1.5 leading-tight">{activePath.title}</h2>
              <div className="flex items-center gap-3.5 text-[13px] text-[hsl(0,0%,65%)] flex-wrap mt-2">
                <span>By Cubemate Team</span>
                <span>&bull;</span>
                <span>{activePath.levelCount} prompts</span>
                <span>&bull;</span>
                <span>{activePath.duration}</span>
              </div>
              <div className="flex gap-1.5 mt-3">
                {activePath.languages.map(l => (
                  <span key={l} className="text-[11px] px-2 py-0.5 rounded bg-[hsl(0,0%,15%)] text-[hsl(0,0%,65%)] font-medium">{l}</span>
                ))}
              </div>
            </div>

            <p className="text-[15px] text-[hsl(0,0%,75%)] leading-relaxed mb-7 pb-5 border-b border-[hsl(0,0%,20%)]">
              {activePath.intro}
            </p>

            {activePath.levels.map((level) => (
              <div key={level.number} className="bg-[hsl(0,0%,12%)] border border-[hsl(0,0%,20%)] rounded-[10px] p-5 mb-3">
                <div className="flex items-center gap-3 mb-2.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold shrink-0 border-2 ${difficultyColors[level.difficulty].bg} ${difficultyColors[level.difficulty].text} ${difficultyColors[level.difficulty].border}`}>
                    {level.number}
                  </div>
                  <div className="text-[15px] font-semibold">{level.title}</div>
                  <div className="text-xs text-[hsl(0,0%,65%)] ml-auto">{level.subtitle}</div>
                </div>
                <div className="bg-[hsl(0,0%,9%)] border border-[hsl(0,0%,20%)] rounded-lg p-3.5 mt-2.5 flex items-start justify-between gap-3">
                  <div className="text-sm text-white leading-relaxed italic flex-1">{level.prompt}</div>
                  <CopyButton text={level.prompt} copyKey={`path-${activePath.id}-${level.number}`} />
                </div>
                <div className="text-xs text-[hsl(263,60%,50%)] mt-2.5 font-medium">{level.tryAlso}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Recipe Detail Modal ────────────────────────────────────────── */}
      {activeRecipe && (
        <div
          className="fixed inset-0 bg-black/70 z-[200] overflow-y-auto p-10 flex justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setActiveRecipeId(null); }}
        >
          <div className="bg-[hsl(0,0%,9%)] border border-[hsl(0,0%,20%)] rounded-2xl max-w-[860px] w-full p-10 relative my-auto">
            <button
              onClick={() => setActiveRecipeId(null)}
              className="absolute top-4 right-4 w-9 h-9 rounded-lg border border-[hsl(0,0%,20%)] bg-[hsl(0,0%,12%)] text-white text-lg cursor-pointer flex items-center justify-center transition-all hover:bg-[hsl(0,0%,15%)]"
            >
              &times;
            </button>

            <div className="mb-7">
              <div className="flex gap-1.5 mb-3">
                {activeRecipe.isPopular && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-pink-500/10 text-pink-500 uppercase tracking-wider">Popular</span>
                )}
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider ${difficultyColors[activeRecipe.difficulty].bg} ${difficultyColors[activeRecipe.difficulty].text}`}>
                  {activeRecipe.difficulty}
                </span>
              </div>
              <h2 className="text-[26px] font-bold mb-1.5 leading-tight">{activeRecipe.title}</h2>
              <div className="flex items-center gap-3.5 text-[13px] text-[hsl(0,0%,65%)] flex-wrap mt-2">
                <span>By Cubemate Team</span>
                <span>&bull;</span>
                <span>{activeRecipe.detail.prompts.length} prompts</span>
                <span>&bull;</span>
                <span>{activeRecipe.detail.duration}</span>
                <span>&bull;</span>
                <span>{activeRecipe.detail.language}</span>
              </div>
            </div>

            {/* The Problem */}
            <div className="mb-7">
              <h3 className="text-[13px] font-semibold mb-3 text-[hsl(263,60%,50%)] uppercase tracking-wider">The Problem</h3>
              <p className="text-[15px] leading-relaxed text-[hsl(0,0%,80%)]">{activeRecipe.detail.problem}</p>
            </div>

            {/* The Approach */}
            <div className="mb-7">
              <h3 className="text-[13px] font-semibold mb-3 text-[hsl(263,60%,50%)] uppercase tracking-wider">The Approach</h3>
              <div>
                {activeRecipe.detail.steps.map((step, i) => (
                  <div key={i} className="flex gap-4 mb-5 items-start">
                    <div className="w-7 h-7 rounded-full bg-[hsl(263,60%,50%,0.15)] text-[hsl(263,60%,50%)] flex items-center justify-center text-[13px] font-bold shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <div>
                      <h4 className="text-[15px] font-semibold mb-1">{step.title}</h4>
                      <p className="text-sm text-[hsl(0,0%,65%)] leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* The Prompts */}
            <div className="mb-7">
              <h3 className="text-[13px] font-semibold mb-3 text-[hsl(263,60%,50%)] uppercase tracking-wider">The Prompts</h3>
              {activeRecipe.detail.prompts.map((prompt, i) => (
                <div key={i} className="bg-[hsl(0,0%,12%)] border border-[hsl(0,0%,20%)] rounded-[10px] p-4 mb-2.5 flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-wider text-[hsl(263,60%,50%)] font-semibold mb-1.5">{prompt.label}</div>
                    <div className="text-sm text-white leading-relaxed italic">{prompt.text}</div>
                  </div>
                  <CopyButton text={prompt.text} copyKey={`recipe-${activeRecipe.id}-${i}`} />
                </div>
              ))}
            </div>

            {/* The Result */}
            <div className="mb-7">
              <h3 className="text-[13px] font-semibold mb-3 text-[hsl(263,60%,50%)] uppercase tracking-wider">The Result</h3>
              <div className="bg-[hsl(0,0%,12%)] border border-[hsl(0,0%,20%)] rounded-[10px] p-5 text-center text-[hsl(0,0%,65%)] text-sm">
                <p>What you'll get:</p>
                <div className="border-2 border-dashed border-[hsl(0,0%,20%)] rounded-lg mt-3 p-10">
                  {activeRecipe.detail.result}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Starter Detail Modal ───────────────────────────────────────── */}
      {activeStarter && (
        <div
          className="fixed inset-0 bg-black/70 z-[200] overflow-y-auto p-10 flex justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setActiveStarterId(null); }}
        >
          <div className="bg-[hsl(0,0%,9%)] border border-[hsl(0,0%,20%)] rounded-2xl max-w-[860px] w-full p-10 relative my-auto">
            <button
              onClick={() => setActiveStarterId(null)}
              className="absolute top-4 right-4 w-9 h-9 rounded-lg border border-[hsl(0,0%,20%)] bg-[hsl(0,0%,12%)] text-white text-lg cursor-pointer flex items-center justify-center transition-all hover:bg-[hsl(0,0%,15%)]"
            >
              &times;
            </button>

            <div className="mb-7">
              <div className="flex gap-1.5 mb-3">
                <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[hsl(263,70%,60%,0.12)] text-[hsl(263,70%,60%)] uppercase tracking-wider">
                  {activeStarter.promptCount} Prompts
                </span>
              </div>
              <h2 className="text-[26px] font-bold mb-1.5 leading-tight">{activeStarter.title} — Try These Prompts</h2>
              <div className="flex items-center gap-3.5 text-[13px] text-[hsl(0,0%,65%)] flex-wrap mt-2">
                <span>By Cubemate</span>
                <span>&bull;</span>
                <span>{activeStarter.duration}</span>
              </div>
            </div>

            <p className="text-[15px] text-[hsl(0,0%,75%)] leading-relaxed mb-7 pb-5 border-b border-[hsl(0,0%,20%)]">
              {activeStarter.intro}
            </p>

            {activeStarter.prompts.map((p) => (
              <div key={p.number} className="bg-[hsl(0,0%,12%)] border border-[hsl(0,0%,20%)] rounded-[10px] p-5 mb-3">
                <div className="flex items-center gap-3 mb-2.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold shrink-0 border-2 ${difficultyColors[p.difficulty].bg} ${difficultyColors[p.difficulty].text} ${difficultyColors[p.difficulty].border}`}>
                    {p.number}
                  </div>
                  <div className="text-[15px] font-semibold">{p.title}</div>
                  <div className="text-xs text-[hsl(0,0%,65%)] ml-auto">{p.subtitle}</div>
                </div>
                <div className="bg-[hsl(0,0%,9%)] border border-[hsl(0,0%,20%)] rounded-lg p-3.5 mt-2.5 flex items-start justify-between gap-3">
                  <div className="text-sm text-white leading-relaxed italic flex-1">{p.prompt}</div>
                  <CopyButton text={p.prompt} copyKey={`starter-${activeStarter.id}-${p.number}`} />
                </div>
                <div className="text-xs text-[hsl(263,60%,50%)] mt-2.5 font-medium">{p.teaches}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidePage;
